/**
 * Upgrade Bundle functionality for cart
 * Handles bundle offers, modal interactions, and adding products to cart
 */

window.PXUTheme = window.PXUTheme || {};

/**
 * Get market-specific discount percentage from bundle data
 * If linked_bundle_price is set, calculates percentage from linked variant prices
 * Otherwise uses the market-specific discount percentage settings
 * @param {Object} bundleData - Bundle data object
 * @returns {number} Discount percentage
 */
window.PXUTheme.getMarketDiscountPercentage = function(bundleData) {
  // If linked bundle price is set, calculate discount from linked variant's prices
  if (bundleData.linked_bundle_price && bundleData.linked_bundle_compare_price && 
      bundleData.linked_bundle_compare_price > 0) {
    const price = bundleData.linked_bundle_price;
    const comparePrice = bundleData.linked_bundle_compare_price;
    // Calculate percentage: ((compare - price) / compare) * 100
    const calculatedPercentage = ((comparePrice - price) / comparePrice) * 100;
    return Math.round(calculatedPercentage);
  }
  
  // Fallback to market-specific discount percentage settings
  const currentMarket = (window.currentMarketHandle || '').toLowerCase();
  let discountPercentage = bundleData.discount_percentage || 0;
  
  if (currentMarket === 'gb' && bundleData.discount_percentage_gb !== null) {
    discountPercentage = bundleData.discount_percentage_gb;
  } else if (currentMarket === 'us' && bundleData.discount_percentage_us !== null) {
    discountPercentage = bundleData.discount_percentage_us;
  } else if (currentMarket === 'eu' && bundleData.discount_percentage_eu !== null) {
    discountPercentage = bundleData.discount_percentage_eu;
  } else if (currentMarket === 'canada' && bundleData.discount_percentage_canada !== null) {
    discountPercentage = bundleData.discount_percentage_canada;
  } else if (currentMarket === 'international' && bundleData.discount_percentage_international !== null) {
    discountPercentage = bundleData.discount_percentage_international;
  }
  
  return discountPercentage;
};

/**
 * Replace [discount] shortcode in text with actual discount percentage
 * @param {string} text - Text containing [discount] shortcode
 * @param {Object} bundleData - Bundle data object
 * @returns {string} Text with [discount] replaced
 */
window.PXUTheme.replaceDiscountShortcode = function(text, bundleData) {
  if (!text || typeof text !== 'string') return text || '';
  const discountPercentage = window.PXUTheme.getMarketDiscountPercentage(bundleData);
  // Round to integer, same as discount badges
  const roundedPercentage = Math.round(discountPercentage);
  return text.replace(/\[discount\]/gi, `${roundedPercentage}%`);
};

/**
 * Check if any bundles remain in the cart and remove cart attribute if none exist
 * @param {string} removingBundleId - Optional: The bundle ID being removed (to exclude from check)
 */
window.PXUTheme.checkAndRemoveBundleCartAttribute = async function(removingBundleId = null) {
  try {
    const response = await fetch('/cart.js', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin'
    });
    const cart = await response.json();
    
    // Check if any bundle parent items remain in the cart
    // (items with _bundle_parent === 'true' and Bundle Upgrade property)
    const remainingBundles = cart.items.filter(item => {
      if (!item.properties) return false;
      const isBundleParent = item.properties['_bundle_parent'] === 'true';
      const hasBundleUpgrade = item.properties['Bundle Upgrade'];
      const bundleId = item.properties['_bundle_id'];
      
      // Exclude the bundle being removed
      if (removingBundleId && bundleId === removingBundleId) {
        return false;
      }
      
      return isBundleParent && hasBundleUpgrade;
    });
    
    // If no bundles remain, remove the cart attribute
    if (remainingBundles.length === 0) {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'xmlhttprequest' },
        body: JSON.stringify({
          attributes: {
            'Bundle Upgrade': ''  // Empty string removes the attribute
          }
        }),
        credentials: 'same-origin'
      });
    }
  } catch (error) {
    console.error('Failed to check/remove Bundle Upgrade cart attribute:', error);
  }
};

window.PXUTheme.UpgradeBundle = class UpgradeBundle {
  constructor() {
    this.activeModal = null;
    this.selectedProducts = new Map();
    this.translations = {};
    this.moneyFormat = document.querySelector('body')?.dataset?.moneyFormat || '£{{amount}}';
    this.parentVariantId = null;
    this.swapVariantId = null;
    this.shouldSwapParent = false;
    this.discountPercentage = 0;
    this.bundleTotalPrice = 0;
    this.linkedBundlePrice = null; // Price from "Linked Bundle Price Accuracy" metafield
    this.linkedBundleComparePrice = null; // Compare price from the linked variant
    
    // =============================================================================
    // NATIVE BUNDLE PROPERTIES
    // For Shopify native bundles - separate tracking from custom bundles
    // =============================================================================
    this.isNativeBundle = false; // Flag to indicate if current bundle is native
    this.nativeBundleData = null; // Stores the native bundle configuration
    this.selectedNativeBundleOptions = {}; // Tracks user's option selections for native bundle
    this.selectedNativeBundleVariantId = null; // The bundle variant ID to add to cart
    
    // =============================================================================
    // BUNDLE REVERT PROPERTIES
    // Stores pre-upgrade cart state to allow reverting bundle upgrades
    // =============================================================================
    this.bundleRevertEnabled = window.bundleRevertEnabled !== false; // Default to true
    this.REVERT_STORAGE_KEY = 'bundle_revert_states';
    
    
    this.loadTranslations();
    this.init();
  }

  init() {
    // Delegate event listeners for better performance
    document.addEventListener('click', (e) => {
      // Bundle offer button click
      if (e.target.closest('[data-bundle-trigger]')) {
        e.preventDefault();
        const trigger = e.target.closest('[data-bundle-trigger]');
        this.openBundleModal(trigger);
      }

      // Modal close buttons
      if (e.target.closest('[data-bundle-modal-close]')) {
        e.preventDefault();
        this.closeBundleModal();
      }

      // Complete bundle button
      if (e.target.closest('[data-bundle-complete]')) {
        e.preventDefault();
        const btn = e.target.closest('[data-bundle-complete]');
        if (!btn.disabled && !btn.classList.contains('loading')) {
          this.completeBundleAddToCart();
        }
      }
    });

    // Variant selection change
    document.addEventListener('change', (e) => {
      if (e.target.matches('[data-bundle-variant]')) {
        const product = e.target.closest('[data-bundle-product]');
        this.updateProductVariant(product, e.target);
      }
    });

    // Close modal on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.activeModal) {
        this.closeBundleModal();
      }
    });
  }
  
  // Set up mobile scroll behavior for bottom sheet effect
  // SIMPLIFIED: Only expand once when scrolling down, never auto-collapse
  // This prevents scroll jumping caused by layout changes during scroll
  setupMobileScrollBehavior(modal) {
    const productsContainer = modal.querySelector('[data-bundle-products]');
    if (!productsContainer) return;
    
    // Check if mobile
    const isMobile = () => window.matchMedia('(max-width: 768px)').matches;
    
    let isExpanded = false;
    const scrollThreshold = 30; // Pixels to scroll before expanding
    
    const handleScroll = () => {
      // Skip if not mobile or already expanded
      if (!isMobile() || isExpanded) return;
      
      const scrollTop = productsContainer.scrollTop;
      
      // Expand only once when scrolled down past threshold
      // Never auto-collapse - prevents scroll jumping
      if (scrollTop > scrollThreshold) {
        modal.classList.add('mobile-expanded');
        isExpanded = true;
        // Remove listener after expanding - no need to track anymore
        productsContainer.removeEventListener('scroll', handleScroll);
      }
    };
    
    // Remove any existing listener before adding new one
    productsContainer.removeEventListener('scroll', handleScroll);
    productsContainer.addEventListener('scroll', handleScroll, { passive: true });
    
    // Reset state when modal opens
    modal.classList.remove('mobile-expanded');
    isExpanded = false;
    productsContainer.scrollTop = 0;
    
    // Store cleanup function
    modal._cleanupMobileScroll = () => {
      productsContainer.removeEventListener('scroll', handleScroll);
    };
  }

  loadTranslations() {
    const translationScript = document.querySelector('[data-bundle-translations]');
    if (translationScript) {
      try {
        this.translations = JSON.parse(translationScript.textContent);
      } catch (e) {
        console.error('Failed to load bundle translations:', e);
      }
    }
  }

  /**
   * Get market-specific discount percentage from bundle data
   * @param {Object} bundleData - Bundle data object
   * @returns {number} Discount percentage
   */
  getMarketDiscountPercentage(bundleData) {
    return window.PXUTheme.getMarketDiscountPercentage(bundleData);
  }

  /**
   * Replace [discount] shortcode in text with actual discount percentage
   * @param {string} text - Text containing [discount] shortcode
   * @param {Object} bundleData - Bundle data object
   * @returns {string} Text with [discount] replaced
   */
  replaceDiscountShortcode(text, bundleData) {
    return window.PXUTheme.replaceDiscountShortcode(text, bundleData);
  }

  async openBundleModal(trigger) {
    const productId = trigger.dataset.productId;
    const variantId = trigger.dataset.variantId;

    if (!productId && !variantId) {
      console.error('No product or variant ID provided');
      return;
    }

    // Store parent variant ID for later use when adding to cart
    this.parentVariantId = variantId ? parseInt(variantId) : null;
    
    // Reset swap info
    this.swapVariantId = null;
    this.shouldSwapParent = false;
    
    // Reset pricing info
    this.discountPercentage = 0;
    this.bundleTotalPrice = 0;
    this.linkedBundlePrice = null;
    this.linkedBundleComparePrice = null;
    
    // Reset native bundle state
    this.isNativeBundle = false;
    this.nativeBundleData = null;
    this.selectedNativeBundleOptions = {};
    this.selectedNativeBundleVariantId = null;

    // Get current market handle
    const currentMarket = (window.currentMarketHandle || '').toLowerCase();

    // =============================================================================
    // BUNDLE DATA RESOLUTION
    // Check for native bundles first, then fall back to custom upgrade bundles
    // =============================================================================
    let bundleData = null;
    
    // Check for NATIVE bundles first (higher priority)
    if (variantId && window.nativeBundlesVariants && window.nativeBundlesVariants[variantId]) {
      bundleData = window.nativeBundlesVariants[variantId];
      this.isNativeBundle = true;
      this.nativeBundleData = bundleData;
    } else if (productId && window.nativeBundles && window.nativeBundles[productId]) {
      bundleData = window.nativeBundles[productId];
      this.isNativeBundle = true;
      this.nativeBundleData = bundleData;
    }
    // Fall back to CUSTOM upgrade bundles
    else if (variantId && window.upgradeBundlesVariants && window.upgradeBundlesVariants[variantId]) {
      const originalBundleData = window.upgradeBundlesVariants[variantId];
      bundleData = originalBundleData;
      
      // Check if there's a swap_variant_id and if that variant has its own bundle
      const swapVariantId = originalBundleData.swap_variant_id;
      if (swapVariantId && window.upgradeBundlesVariants[swapVariantId]) {
        // Swap variant has its own bundle - use that bundle data and enable swap
        bundleData = window.upgradeBundlesVariants[swapVariantId];
        this.swapVariantId = parseInt(swapVariantId);
        this.shouldSwapParent = true;
      }
    } else if (productId && window.upgradeBundles && window.upgradeBundles[productId]) {
      bundleData = window.upgradeBundles[productId];
    }

    if (!bundleData) {
      console.error('Bundle data not found for product or variant:', { productId, variantId });
      return;
    }

    // Check target markets - if specified, only show bundle to those markets
    if (bundleData.target_markets && bundleData.target_markets.length > 0) {
      const targetMarkets = bundleData.target_markets.map(m => m.toLowerCase());
      if (!targetMarkets.includes(currentMarket)) {
        return;
      }
    }

    // =============================================================================
    // PRICING SETUP
    // Native bundles get pricing from the bundle product itself
    // Custom bundles use discount percentage or linked bundle price
    // =============================================================================
    if (this.isNativeBundle) {
      // Native bundle pricing comes from the bundle product
      const nativeBundle = bundleData.native_bundle;
      if (nativeBundle) {
        this.linkedBundlePrice = nativeBundle.price || null;
        this.linkedBundleComparePrice = nativeBundle.compare_at_price || null;
        // Calculate discount percentage from native bundle prices
        if (this.linkedBundleComparePrice && this.linkedBundleComparePrice > this.linkedBundlePrice) {
          this.discountPercentage = Math.round(
            ((this.linkedBundleComparePrice - this.linkedBundlePrice) / this.linkedBundleComparePrice) * 100
          );
        }
      }
    } else {
      // Custom bundle pricing
      const discountPercentage = window.PXUTheme.getMarketDiscountPercentage(bundleData);
      this.discountPercentage = discountPercentage;
      this.bundleTotalPrice = bundleData.bundle_total_price || 0;
      this.linkedBundlePrice = bundleData.linked_bundle_price || null;
      this.linkedBundleComparePrice = bundleData.linked_bundle_compare_price || null;
    }

    // Use the single shared modal
    const modal = document.getElementById('upgrade-bundle-modal-cart');
    if (!modal) {
      console.error('Modal not found');
      return;
    }

    // Populate modal with bundle data
    try {
      // Set activeModal BEFORE populateModal so updateCompleteBundleButton can access it
      this.activeModal = modal;
      
      // Use different populate method for native vs custom bundles
      if (this.isNativeBundle) {
        // Add class to identify native bundle modal
        modal.classList.add('is-native-bundle');
        await this.populateNativeBundleModal(modal, bundleData, productId);
      } else {
        modal.classList.remove('is-native-bundle');
        await this.populateModal(modal, bundleData, productId);
      }
      
      // Set up mobile scroll behavior for bottom sheet effect
      this.setupMobileScrollBehavior(modal);
      
      modal.classList.add('active');
      document.body.classList.add('noscroll');
    } catch (error) {
      console.error('Error opening bundle modal:', error);
      this.activeModal = null; // Reset on error
    }
  }

  closeBundleModal() {
    if (this.activeModal) {
      this.activeModal.classList.remove('active');
      this.activeModal.classList.remove('mobile-expanded'); // Reset mobile expanded state
      document.body.classList.remove('noscroll');
      
      // Clean up mobile scroll listener
      if (this.activeModal._cleanupMobileScroll) {
        this.activeModal._cleanupMobileScroll();
      }
      
      // Reset button state
      const completeBtn = this.activeModal.querySelector('[data-bundle-complete]');
      if (completeBtn) {
        completeBtn.disabled = false;
        completeBtn.classList.remove('loading');
        const completeBtnText = completeBtn.querySelector('span');
        const addingText = this.translations.adding || 'Adding...';
        if (completeBtnText && completeBtnText.textContent === addingText) {
          completeBtnText.textContent = this.translations.complete_bundle || 'Complete Bundle';
        }
      }
      
      // Remove any hardcoded heights (let CSS handle mobile)
      const contentSection = this.activeModal.querySelector('.upgrade-bundle-modal__content-section');
      if (contentSection) {
        contentSection.style.height = '';
      }
      
      // Clear selected products and swap info after a delay
      setTimeout(() => {
        this.selectedProducts.clear();
        this.activeModal = null;
        this.swapVariantId = null;
        this.shouldSwapParent = false;
      }, 300);
    }
  }

  async populateModal(modal, bundleData, productId) {
    const productsContainer = modal.querySelector('[data-bundle-products]');
    productsContainer.innerHTML = '';

    // Update bundle image
    const imageWrapper = modal.querySelector('[data-bundle-image-wrapper]');
    const imageSection = modal.querySelector('.upgrade-bundle-modal__image-section');
    const contentSection = modal.querySelector('.upgrade-bundle-modal__content-section');
    
    // Function to check if we're on mobile
    const isMobile = () => {
      return window.matchMedia('(max-width: 768px)').matches;
    };
    
    // Function to sync heights - make content section match image section (desktop only)
    const syncHeights = () => {
      // Only sync heights on desktop, not mobile
      if (isMobile()) {
        // On mobile, remove any hardcoded height and let CSS handle it
        if (contentSection) {
          contentSection.style.height = '';
        }
        return;
      }
      
      if (imageSection && contentSection) {
        const imageHeight = imageSection.offsetHeight;
        const maxViewportHeight = window.innerHeight * 0.9; // 90vh
        // Use the smaller of image height or 90vh to ensure footer stays visible
        const constrainedHeight = Math.min(imageHeight, maxViewportHeight);
        contentSection.style.height = `${constrainedHeight}px`;
        contentSection.style.maxHeight = `${maxViewportHeight}px`;
      }
    };
    
    if (imageWrapper && bundleData.bundle_image) {
      const img = document.createElement('img');
      // Use mobile image on mobile devices if available, otherwise fall back to desktop
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      const imageSrc = (isMobile && bundleData.bundle_mobile_image) 
        ? bundleData.bundle_mobile_image 
        : bundleData.bundle_image;
      img.src = imageSrc;
      img.alt = bundleData.bundle_name;
      img.loading = 'lazy';
      img.style.width = '100%';
      img.style.height = '100%';
      img.style.objectFit = 'cover';
      img.style.display = 'block';
      
      imageWrapper.innerHTML = '';
      imageWrapper.appendChild(img);
      
      // Sync heights after image loads (desktop only)
      img.onload = () => {
        setTimeout(syncHeights, 100);
      };
      
      // Sync heights on window resize (desktop only)
      if (imageSection && contentSection) {
        const resizeObserver = new ResizeObserver(() => {
          syncHeights();
        });
        resizeObserver.observe(imageSection);
        
        // Also sync after products are rendered (desktop only)
        setTimeout(syncHeights, 300);
        
        // Listen for window resize to handle mobile/desktop switching
        window.addEventListener('resize', syncHeights);
      }
    } else if (imageWrapper) {
      imageWrapper.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #999;">No image</div>';
    }

    // Update modal header with bundle info
    const modalTitle = modal.querySelector('[data-bundle-title]');
    const modalDiscount = modal.querySelector('[data-bundle-discount]');
    const modalDescription = modal.querySelector('[data-bundle-description]');
    
    if (modalTitle) modalTitle.textContent = bundleData.bundle_name;
    if (modalDiscount) {
      if (bundleData.bundle_discount_text) {
        // Replace [discount] shortcode with actual discount percentage
        const discountText = this.replaceDiscountShortcode(bundleData.bundle_discount_text, bundleData);
        modalDiscount.textContent = discountText;
        modalDiscount.style.display = 'inline-block';
      } else {
        modalDiscount.style.display = 'none';
      }
    }
    if (modalDescription) {
      if (bundleData.bundle_description) {
        modalDescription.innerHTML = bundleData.bundle_description;
        modalDescription.style.display = 'block';
      } else {
        modalDescription.style.display = 'none';
      }
    }

    // Get current cart items
    const cart = await this.fetchCart();
    // Only exclude items that have _bundle_id (they belong to a bundle)
    // Items with parent_relationship but no _bundle_id are nested due to discount codes, 
    // but should still be considered as "in cart" for bundle purposes
    const standaloneCartItems = cart.items.filter(item => {
      const hasBundleId = item.properties && item.properties['_bundle_id'];
      return !hasBundleId;
    });
    const cartVariantIds = standaloneCartItems.map(item => String(item.variant_id));

    // Bundle products are directly in bundleData.bundle_products
    if (!bundleData.bundle_products || bundleData.bundle_products.length === 0) {
      console.error('No bundle products found');
      productsContainer.innerHTML = '<p style="padding: 20px; text-align: center;">No products found in this bundle.</p>';
      return;
    }

    const bundleProducts = bundleData.bundle_products;
    let masterProduct = bundleData.master_product;
    
    // If in swap mode, create a product object representing the swap variant
    if (this.shouldSwapParent && this.swapVariantId && masterProduct) {
      const swapVariant = masterProduct.variants.find(v => 
        v.id === this.swapVariantId || String(v.id) === String(this.swapVariantId)
      );
      if (swapVariant) {
        masterProduct = {
          ...masterProduct,
          title: swapVariant.title !== 'Default Title' ? `${masterProduct.title} - ${swapVariant.title}` : masterProduct.title,
          featured_image: swapVariant.image || masterProduct.featured_image,
          price: swapVariant.price,
          compare_at_price: swapVariant.compare_at_price,
          variants: [swapVariant],
          _isSwapVariant: true
        };
      }
    } else if (this.parentVariantId && masterProduct && masterProduct.variants) {
      // No swap, but we have a specific parent variant - show that variant's info
      const parentVariant = masterProduct.variants.find(v => 
        v.id === this.parentVariantId || String(v.id) === String(this.parentVariantId)
      );
      if (parentVariant) {
        masterProduct = {
          ...masterProduct,
          title: parentVariant.title !== 'Default Title' ? `${masterProduct.title} - ${parentVariant.title}` : masterProduct.title,
          featured_image: parentVariant.image || masterProduct.featured_image,
          price: parentVariant.price,
          compare_at_price: parentVariant.compare_at_price,
          variants: [parentVariant]
        };
      }
    }
    
    // Separate products into "not in cart" and "already in cart"
    const notInCart = [];
    const inCart = [];

    bundleProducts.forEach(product => {
      // Check if variants exists and is an array
      if (!product.variants || !Array.isArray(product.variants)) {
        console.error('Product missing variants array:', product);
        return;
      }
      
      // Check if product is in cart - only check allowed variants if specified
      const allowedIds = Array.isArray(product.allowed_variant_ids)
        ? product.allowed_variant_ids.map(id => String(id))
        : null;
      
      const variantsToCheck = allowedIds 
        ? product.variants.filter(v => allowedIds.includes(String(v.id)))
        : product.variants;
      
      const productInCart = variantsToCheck.some(variant => 
        cartVariantIds.includes(String(variant.id))
      );

      if (productInCart) {
        inCart.push(product);
      } else {
        notInCart.push(product);
      }
    });

    // If all products are in cart, we shouldn't show the bundle
    // But if we're here, render them anyway (this check should be done before showing the offer)
    
    // Sort products not in cart: those with variant selectors first
    notInCart.sort((a, b) => {
      // Calculate variant count for each product
      const aAllowedIds = Array.isArray(a.allowed_variant_ids) ? a.allowed_variant_ids.length : null;
      const aVariantCount = aAllowedIds !== null ? aAllowedIds : (a.variants?.length || 0);
      
      const bAllowedIds = Array.isArray(b.allowed_variant_ids) ? b.allowed_variant_ids.length : null;
      const bVariantCount = bAllowedIds !== null ? bAllowedIds : (b.variants?.length || 0);
      
      // Products with more than 1 variant (will have selector) come first
      const aHasSelector = aVariantCount > 1;
      const bHasSelector = bVariantCount > 1;
      
      if (aHasSelector && !bHasSelector) return -1; // a comes first
      if (!aHasSelector && bHasSelector) return 1;  // b comes first
      return 0; // Keep original order if both have or both don't have selectors
    });
    
    // Render products not in cart (sorted: variant selectors first) and auto-select them
    notInCart.forEach(product => {
      // Check if product has variant selector (more than 1 allowed variant)
      const allowedIds = Array.isArray(product.allowed_variant_ids) ? product.allowed_variant_ids.length : null;
      const variantCount = allowedIds !== null ? allowedIds : (product.variants?.length || 0);
      const hasVariantSelector = variantCount > 1;
      
      // Products without selectors get 'will-add' badge
      const badgeType = hasVariantSelector ? null : 'will-add';
      const productEl = this.createProductElement(product, false, cartVariantIds, badgeType);
      
      // Highlight products with variant selector
      if (hasVariantSelector) {
        productEl.classList.add('has-selector');
      }
      
      productsContainer.appendChild(productEl);
    });

    // Render products already in cart (with badge) at the end
    inCart.forEach(product => {
      const productEl = this.createProductElement(product, true, cartVariantIds);
      productsContainer.appendChild(productEl);
    });
    
    // Render master product at the end
    if (masterProduct && masterProduct.variants && Array.isArray(masterProduct.variants) && masterProduct.variants.length > 0) {
      // Use 'upgrade' badge type if this is a swap variant
      const isSwapVariant = masterProduct._isSwapVariant;
      const badgeType = isSwapVariant ? 'upgrade' : null;
      // Don't grey out swap variants - pass isInCart as false for swaps
      // Pass isMasterProduct=true to show only compare_at_price (no discount shown)
      const productEl = this.createProductElement(masterProduct, !isSwapVariant, cartVariantIds, badgeType, true);
      productEl.dataset.isMasterProduct = 'true';
      productsContainer.appendChild(productEl);
    }

    // Auto-select all products not in cart
    notInCart.forEach(product => {
      const renderedProductEl = productsContainer.querySelector(`[data-product-id="${product.id}"]`);
      if (renderedProductEl) {
        this.addProductToSelection(renderedProductEl);
      }
    });

    // Update button text based on what's in cart
    const completeBtn = modal.querySelector('[data-bundle-complete]');
    const completeBtnText = completeBtn.querySelector('span');
    if (inCart.length > 0 || masterProduct) {
      // Some items already in cart
      completeBtnText.textContent = this.translations.complete_bundle || 'Complete Bundle';
    } else {
      // No items in cart
      completeBtnText.textContent = this.translations.add_bundle_button || 'Add Bundle';
    }

    this.updateCompleteBundleButton();
    
    // Sync heights after all content is rendered (desktop only)
    setTimeout(() => {
      const imageSection = modal.querySelector('.upgrade-bundle-modal__image-section');
      const contentSection = modal.querySelector('.upgrade-bundle-modal__content-section');
      
      // Only sync on desktop
      if (!window.matchMedia('(max-width: 768px)').matches) {
        if (imageSection && contentSection) {
          const imageHeight = imageSection.offsetHeight;
          contentSection.style.height = `${imageHeight}px`;
        }
      } else {
        // On mobile, ensure no hardcoded height
        if (contentSection) {
          contentSection.style.height = '';
        }
      }
    }, 100);
  }

  // =============================================================================
  // NATIVE BUNDLE MODAL
  // Populate modal for Shopify native bundles
  // Shows option selectors based on bundle options, not individual products
  // =============================================================================
  async populateNativeBundleModal(modal, bundleData, productId) {
    const productsContainer = modal.querySelector('[data-bundle-products]');
    productsContainer.innerHTML = '';

    // Update bundle image (same as custom bundles)
    const imageWrapper = modal.querySelector('[data-bundle-image-wrapper]');
    const imageSection = modal.querySelector('.upgrade-bundle-modal__image-section');
    const contentSection = modal.querySelector('.upgrade-bundle-modal__content-section');
    
    const isMobile = () => window.matchMedia('(max-width: 768px)').matches;
    
    const syncHeights = () => {
      if (isMobile()) {
        if (contentSection) contentSection.style.height = '';
        return;
      }
      if (imageSection && contentSection) {
        const imageHeight = imageSection.offsetHeight;
        const maxViewportHeight = window.innerHeight * 0.9;
        const constrainedHeight = Math.min(imageHeight, maxViewportHeight);
        contentSection.style.height = `${constrainedHeight}px`;
        contentSection.style.maxHeight = `${maxViewportHeight}px`;
      }
    };
    
    if (imageWrapper && bundleData.bundle_image) {
      const img = document.createElement('img');
      const isMobileView = window.matchMedia('(max-width: 768px)').matches;
      const imageSrc = (isMobileView && bundleData.bundle_mobile_image) 
        ? bundleData.bundle_mobile_image 
        : bundleData.bundle_image;
      img.src = imageSrc;
      img.alt = bundleData.bundle_name;
      img.loading = 'lazy';
      img.style.width = '100%';
      img.style.height = '100%';
      img.style.objectFit = 'cover';
      img.style.display = 'block';
      
      imageWrapper.innerHTML = '';
      imageWrapper.appendChild(img);
      
      img.onload = () => setTimeout(syncHeights, 100);
      
      if (imageSection && contentSection) {
        const resizeObserver = new ResizeObserver(syncHeights);
        resizeObserver.observe(imageSection);
        setTimeout(syncHeights, 300);
        window.addEventListener('resize', syncHeights);
      }
    } else if (imageWrapper) {
      imageWrapper.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #999;">No image</div>';
    }

    // Update modal header with native bundle info
    const modalTitle = modal.querySelector('[data-bundle-title]');
    const modalDiscount = modal.querySelector('[data-bundle-discount]');
    const modalDescription = modal.querySelector('[data-bundle-description]');
    
    if (modalTitle) modalTitle.textContent = bundleData.bundle_name;
    if (modalDiscount) {
      if (bundleData.bundle_discount_text) {
        // Calculate discount from native bundle prices for [discount] shortcode
        const nativeBundle = bundleData.native_bundle;
        const bundleDataForDiscount = {
          ...bundleData,
          linked_bundle_price: nativeBundle?.price,
          linked_bundle_compare_price: nativeBundle?.compare_at_price
        };
        const discountText = this.replaceDiscountShortcode(bundleData.bundle_discount_text, bundleDataForDiscount);
        modalDiscount.textContent = discountText;
        modalDiscount.style.display = 'inline-block';
      } else {
        modalDiscount.style.display = 'none';
      }
    }
    if (modalDescription) {
      if (bundleData.bundle_description) {
        modalDescription.innerHTML = bundleData.bundle_description;
        modalDescription.style.display = 'block';
      } else {
        modalDescription.style.display = 'none';
      }
    }

    const nativeBundle = bundleData.native_bundle;
    if (!nativeBundle) {
      console.error('Native bundle product not found');
      productsContainer.innerHTML = '<p style="padding: 20px; text-align: center;">Bundle configuration error.</p>';
      return;
    }

    // Initialize option selections based on parent variant (if in cart)
    // Try to match parent variant's options to bundle options
    const cart = await this.fetchCart();
    const parentItem = this.parentVariantId 
      ? cart.items.find(item => item.variant_id === this.parentVariantId)
      : null;
    
    // Initialize option selections
    this.selectedNativeBundleOptions = {};
    
    // Pre-select first available option for each bundle option
    nativeBundle.options.forEach((option, index) => {
      const optionPosition = index + 1;
      // Try to match parent variant's color/option to bundle option
      if (parentItem && parentItem.options_with_values) {
        const matchingParentOption = parentItem.options_with_values.find(po => {
          // Match by option name or by value being in the bundle option values
          return option.values.includes(po.value);
        });
        if (matchingParentOption) {
          this.selectedNativeBundleOptions[optionPosition] = matchingParentOption.value;
          return;
        }
      }
      // Default to first value
      this.selectedNativeBundleOptions[optionPosition] = option.values[0];
    });

    // =============================================================================
    // RENDER OPTION PRODUCTS
    // For each bundle option, show the linked product with option selector
    // Uses the same template as custom bundles for consistent styling
    // IMPORTANT: If option product matches master product, it's locked to cart selection
    // =============================================================================
    const template = document.getElementById('upgrade-bundle-product-template');
    const masterProductId = bundleData.master_product?.id;
    
    // Collect products first, then reorder: products with selectors first
    const productsWithSelectors = [];
    const productsWithoutSelectors = [];
    
    nativeBundle.options.forEach((option, index) => {
      const optionPosition = index + 1;
      const optionProduct = bundleData.option_products?.[optionPosition];
      
      // Skip if no option product is linked (can't show anything meaningful)
      if (!optionProduct) return;
      
      // Check if this option product is the same as the master/parent product in cart
      // If so, the option is locked to the cart item's selection
      const isParentProduct = optionProduct.id === masterProductId;
      
      // Get currently selected value for this option
      const selectedValue = this.selectedNativeBundleOptions[optionPosition];
      
      // Find matching variant from option product
      // Try to find the best match - prioritize variants that have images
      let matchingVariant = null;
      if (optionProduct.variants) {
        // First, find all variants that match the selected value
        const matchingVariants = optionProduct.variants.filter(v => {
          return v.option1 === selectedValue || v.option2 === selectedValue || v.option3 === selectedValue || v.title === selectedValue;
        });
        
        // Prefer variant with an image, otherwise take the first match
        if (matchingVariants.length > 0) {
          matchingVariant = matchingVariants.find(v => v.image) || matchingVariants[0];
        }
        
        // If no match found, fall back to first variant
        if (!matchingVariant) {
          matchingVariant = optionProduct.variants[0];
        }
      }
      
      // Clone the template
      const productEl = template.content.cloneNode(true).querySelector('[data-bundle-product]');
      productEl.classList.add('native-bundle-option');
      productEl.dataset.optionPosition = optionPosition;
      productEl.dataset.optionName = option.name;
      productEl.dataset.productId = String(optionProduct.id);
      productEl.dataset.variantId = matchingVariant ? String(matchingVariant.id) : '';
      productEl.dataset.price = matchingVariant ? String(matchingVariant.price) : '0';
      
      // For parent product, find addon_product and swap_variant
      // Data comes directly from Liquid output in master_product.variants
      let parentVariantData = null;
      let addonProduct = null;
      let swapVariant = null;
      
      if (isParentProduct && this.parentVariantId && bundleData.master_product?.variants) {
        // Try both number and string comparison for variant ID matching
        parentVariantData = bundleData.master_product.variants.find(v => 
          v.id === this.parentVariantId || String(v.id) === String(this.parentVariantId)
        );
        
        if (parentVariantData) {
          // Read directly from Liquid-rendered data
          addonProduct = parentVariantData.addon_product;
          swapVariant = parentVariantData.swap_variant;
        }
      }
      
      // Set image
      const img = productEl.querySelector('img');
      let imageUrl = '';
      
      if (isParentProduct) {
        // For parent product:
        // 1. Use swap_variant image if available (for display purposes)
        // 2. Otherwise use the cart item's image
        if (swapVariant && swapVariant.image) {
          imageUrl = swapVariant.image;
        } else if (parentItem && parentItem.image) {
          imageUrl = parentItem.image;
        } else {
          imageUrl = matchingVariant?.image || optionProduct.featured_image || '';
        }
      } else {
        // For other products, use variant image or product featured image
        imageUrl = matchingVariant?.image || optionProduct.featured_image || '';
      }
      
      img.src = imageUrl;
      img.alt = optionProduct.title;
      
      // Store variant image in dataset for easy access when switching options
      productEl.dataset.currentVariantImage = imageUrl;
      
      // Set title - include selected variant for parent product
      if (isParentProduct && selectedValue) {
        productEl.querySelector('.upgrade-bundle-product__title').textContent = `${optionProduct.title} - ${selectedValue}`;
      } else {
        productEl.querySelector('.upgrade-bundle-product__title').textContent = optionProduct.title;
      }
      
      // Set price
      const price = matchingVariant ? parseInt(matchingVariant.price) : parseInt(optionProduct.price);
      const comparePrice = matchingVariant?.compare_at_price ? parseInt(matchingVariant.compare_at_price) : null;
      
      productEl.querySelector('.upgrade-bundle-product__price-current').textContent = this.formatMoney(price);
      if (comparePrice && comparePrice > price) {
        productEl.querySelector('.upgrade-bundle-product__price-compare').textContent = this.formatMoney(comparePrice);
      } else {
        productEl.querySelector('.upgrade-bundle-product__price-compare').style.display = 'none';
      }
      
      // Handle status badges and variant selector
      const selectorContainer = productEl.querySelector('[data-variant-selector]');
      
      if (isParentProduct) {
        // This is the parent product - show with "Included" badge, no greyed out styling
        productEl.classList.add('native-bundle-parent');
        // Don't add 'in-cart' class to avoid greyed out styling
        
        // Use the last status badge, remove the first
        const statusEls = productEl.querySelectorAll('[data-product-status]');
        if (statusEls.length > 1) {
          statusEls[0].remove();
        }
        const statusEl = productEl.querySelector('[data-product-status]');
        if (statusEl) {
          statusEl.textContent = 'Included';
          statusEl.classList.remove('already-in-cart');
          statusEl.classList.add('included');
        }
        
        // Add addon product strip if exists (e.g., "+ UV Case")
        if (addonProduct && addonProduct.title) {
          const addonStrip = document.createElement('div');
          addonStrip.className = 'upgrade-bundle-product__addon-strip';
          addonStrip.innerHTML = `
            <span class="addon-plus">+</span>
            ${addonProduct.featured_image ? `<img src="${addonProduct.featured_image}" alt="${addonProduct.title}" class="addon-image" />` : ''}
            <span class="addon-title">${addonProduct.title}</span>
          `;
          // Insert in the content area, after the price section
          const contentEl = productEl.querySelector('.upgrade-bundle-product__content');
          if (contentEl) {
            const priceEl = contentEl.querySelector('.upgrade-bundle-product__price');
            if (priceEl && priceEl.nextSibling) {
              contentEl.insertBefore(addonStrip, priceEl.nextSibling);
            } else {
              contentEl.appendChild(addonStrip);
            }
          }
        }
        
        // Hide variant selector - option is locked to cart selection
        selectorContainer.style.display = 'none';
        
        // Parent products go to "without selectors" group
        productsWithoutSelectors.push(productEl);
      } else {
        // Regular option product - show selector and "Included" badge
        // Add class to identify products with selectors
        productEl.classList.add('has-selector');
        
        // Update status badge to "Included - please make a selection"
        const statusEls = productEl.querySelectorAll('[data-product-status]');
        if (statusEls.length > 0) {
          statusEls.forEach(el => {
            el.innerHTML = 'Included<span class="selection-hint"> - please make a selection</span>';
            el.classList.remove('will-be-added', 'already-in-cart');
            el.classList.add('included');
          });
        }
        
        // Render option swatches
        selectorContainer.dataset.nativeOptionSelector = optionPosition;
        this.renderNativeBundleOptionSelector(selectorContainer, option, optionPosition, optionProduct);
        
        // Products with selectors go to "with selectors" group
        productsWithSelectors.push(productEl);
      }
    });

    // =============================================================================
    // RENDER FIXED PRODUCTS
    // Products that are always included (no selection needed)
    // Uses the same template as custom bundles for consistent styling
    // Fixed products don't have selectors, so they go to "without selectors" group
    // =============================================================================
    if (bundleData.fixed_variants && bundleData.fixed_variants.length > 0) {
      bundleData.fixed_variants.forEach(fixedVariant => {
        // Clone the template
        const fixedEl = template.content.cloneNode(true).querySelector('[data-bundle-product]');
        fixedEl.classList.add('native-bundle-fixed');
        fixedEl.dataset.variantId = String(fixedVariant.id);
        fixedEl.dataset.price = String(fixedVariant.price);
        
        // Use product title + variant title for display
        const displayTitle = fixedVariant.title && fixedVariant.title !== 'Default Title'
          ? `${fixedVariant.product_title} - ${fixedVariant.title}`
          : fixedVariant.product_title;
        
        // Set image
        const img = fixedEl.querySelector('img');
        img.src = fixedVariant.image || '';
        img.alt = displayTitle;
        
        // Set title
        fixedEl.querySelector('.upgrade-bundle-product__title').textContent = displayTitle;
        
        // Set price
        const price = parseInt(fixedVariant.price);
        const comparePrice = fixedVariant.compare_at_price ? parseInt(fixedVariant.compare_at_price) : null;
        
        fixedEl.querySelector('.upgrade-bundle-product__price-current').textContent = this.formatMoney(price);
        if (comparePrice && comparePrice > price) {
          fixedEl.querySelector('.upgrade-bundle-product__price-compare').textContent = this.formatMoney(comparePrice);
        } else {
          fixedEl.querySelector('.upgrade-bundle-product__price-compare').style.display = 'none';
        }
        
        // Set status badge to "Included" for native bundles
        // Template has two badge elements - use the last one, remove the first
        const statusEls = fixedEl.querySelectorAll('[data-product-status]');
        if (statusEls.length > 1) {
          statusEls[0].remove(); // Remove first one
        }
        const statusEl = fixedEl.querySelector('[data-product-status]');
        if (statusEl) {
          statusEl.textContent = 'Included';
          statusEl.classList.remove('will-be-added');
          statusEl.classList.add('included');
        }
        
        // Hide variant selector for fixed products
        const variantSelector = fixedEl.querySelector('[data-variant-selector]');
        if (variantSelector) {
          variantSelector.style.display = 'none';
        }
        
        // Fixed products don't have selectors, add to "without selectors" group
        productsWithoutSelectors.push(fixedEl);
      });
    }
    
    // Render products: those with selectors first, then those without
    productsWithSelectors.forEach(productEl => {
      productsContainer.appendChild(productEl);
    });
    productsWithoutSelectors.forEach(productEl => {
      productsContainer.appendChild(productEl);
    });

    // =============================================================================
    // MASTER PRODUCT NOTE
    // The master/parent product is now rendered as part of the option products
    // when it matches one of the option_products (isParentProduct check above).
    // It's shown with "Already in your cart" badge and no selector.
    // This provides a cleaner UX - the parent product appears in the correct
    // position in the bundle options rather than at the end.
    // =============================================================================

    // Update complete button
    const completeBtn = modal.querySelector('[data-bundle-complete]');
    const completeBtnText = completeBtn.querySelector('span');
    completeBtnText.textContent = this.translations.complete_bundle || 'Complete Bundle';
    
    // Update the selected bundle variant based on current option selections
    this.updateSelectedNativeBundleVariant();
    this.updateNativeBundleButton();
    
    // Sync heights after rendering
    setTimeout(syncHeights, 100);
  }

  /**
   * Render option selector for native bundle option
   * Creates swatches/buttons matching the custom bundle styling
   */
  renderNativeBundleOptionSelector(container, option, optionPosition, optionProduct) {
    if (!container) return;
    
    const selectedValue = this.selectedNativeBundleOptions[optionPosition];
    
    // For native bundles, option.name is the bundle option name (e.g., "SURI 2.0 Electric Toothbrush")
    // We should treat it as a color option if:
    // 1. The option name contains color/colour, OR
    // 2. The option product has color-related options, OR
    // 3. The values look like color names (common colors)
    const isColorOption = (name, values) => {
      const lowerName = name.toLowerCase();
      if (lowerName.includes('color') || lowerName.includes('colour')) return true;
      
      // Check if option product has a colour option
      if (optionProduct && optionProduct.options) {
        const hasColourOption = optionProduct.options.some(opt => 
          opt.name.toLowerCase().includes('color') || opt.name.toLowerCase().includes('colour')
        );
        if (hasColourOption) return true;
      }
      
      // Check if values are common color names
      const colorNames = ['black', 'white', 'red', 'blue', 'green', 'yellow', 'orange', 'purple', 'pink', 
        'grey', 'gray', 'brown', 'navy', 'teal', 'mint', 'coral', 'charcoal', 'fog', 'thyme', 
        'winter fern', 'sea mist', 'morning waves', 'midnight black', 'sunset bloom'];
      if (values && values.length > 0) {
        const matchesColor = values.some(v => 
          colorNames.some(c => v.toLowerCase().includes(c))
        );
        if (matchesColor) return true;
      }
      
      return false;
    };
    
    const useColorSwatches = isColorOption(option.name, option.values);
    
    // Create wrapper matching custom bundle structure
    const optionWrapper = document.createElement('div');
    optionWrapper.className = 'bundle-variant-options';
    
    // Add option title - use "Pick your colour" for color swatches
    const optionTitle = document.createElement('div');
    optionTitle.className = 'bundle-variant-option-title';
    if (useColorSwatches) {
      optionTitle.textContent = (this.translations.pick_your_colour || 'Pick your colour');
    } else {
      optionTitle.textContent = option.name;
    }
    optionWrapper.appendChild(optionTitle);
    
    // Create swatch container matching product page structure
    const swatchContainer = document.createElement('div');
    swatchContainer.className = 'swatch';
    if (useColorSwatches) {
      swatchContainer.setAttribute('data-color-option', '');
      swatchContainer.setAttribute('data-selection-step', 'true');
    }
    
    const optionsContainer = document.createElement('div');
    optionsContainer.className = 'swatch__options';
    if (useColorSwatches) {
      optionsContainer.classList.add('colour');
    }
    
    option.values.forEach(value => {
      // Find variant data for this option value (if option product exists)
      let variantImage = null;
      let variantPrice = null;
      let variantComparePrice = null;
      let variantAvailable = true;
      let matchingVariant = null;
      
      if (optionProduct && optionProduct.variants) {
        // Find all variants matching this option value
        const matchingVariants = optionProduct.variants.filter(v => 
          v.option1 === value || v.option2 === value || v.option3 === value || v.title === value
        );
        
        // Prefer variant with an image
        if (matchingVariants.length > 0) {
          matchingVariant = matchingVariants.find(v => v.image) || matchingVariants[0];
        }
        
        if (matchingVariant) {
          variantImage = matchingVariant.image || optionProduct.featured_image;
          variantPrice = matchingVariant.price;
          variantComparePrice = matchingVariant.compare_at_price;
          variantAvailable = matchingVariant.available !== false;
        }
      }
      
      const colorHandle = value.toLowerCase().replace(/\s+/g, '-');
      const inputId = `native-option-${optionPosition}-${colorHandle}`;
      
      // Create swatch wrapper matching product page structure
      const swatchWrapper = document.createElement('div');
      swatchWrapper.className = `product-swatch colour ${colorHandle}`;
      swatchWrapper.setAttribute('data-variant-option-value-wrapper', '');
      
      // Create radio input
      const input = document.createElement('input');
      input.type = 'radio';
      input.className = `swatch--${colorHandle} native-bundle-option-input`;
      input.name = `native-option-${optionPosition}`;
      input.value = value;
      input.id = inputId;
      input.setAttribute('aria-label', value);
      input.setAttribute('tabindex', '0');
      input.dataset.optionPosition = String(optionPosition);
      input.dataset.variantImage = variantImage || '';
      input.dataset.price = variantPrice ? String(variantPrice) : '';
      input.dataset.comparePrice = variantComparePrice ? String(variantComparePrice) : '';
      
      if (value === selectedValue) {
        input.checked = true;
      }
      
      if (isColorOption(option.name)) {
        // Color swatch with checkmark structure
        const swatchElement = document.createElement('div');
        swatchElement.className = `swatch-element color ${colorHandle}-swatch`;
        
        const label = document.createElement('label');
        label.htmlFor = inputId;
        label.className = `swatch-${colorHandle}`;
        label.setAttribute('data-variant-option-value-label', '');
        label.setAttribute('data-swapper', colorHandle);
        label.setAttribute('data-title', value);
        
        // Add checkmark structure
        const roundContainer = document.createElement('div');
        roundContainer.className = 'swatch__round--container';
        
        const checkmark = document.createElement('img');
        checkmark.className = 'swatch__checkmark';
        checkmark.src = 'https://cdn.shopify.com/s/files/1/0502/5929/6444/files/checkbox.svg?v=1700570579';
        checkmark.alt = 'checkmark';
        
        roundContainer.appendChild(checkmark);
        label.appendChild(roundContainer);
        swatchElement.appendChild(label);
        
        swatchWrapper.appendChild(input);
        swatchWrapper.appendChild(swatchElement);
      } else {
        // Non-color options: simple label structure
        const label = document.createElement('label');
        label.htmlFor = inputId;
        label.textContent = value;
        
        swatchWrapper.appendChild(input);
        swatchWrapper.appendChild(label);
      }
      
      // Disable if not available
      if (!variantAvailable) {
        input.disabled = true;
        if (useColorSwatches) {
          const swatchElement = swatchWrapper.querySelector('.swatch-element');
          if (swatchElement) {
            swatchElement.style.opacity = '0.3';
            swatchElement.style.pointerEvents = 'none';
          }
        } else {
          const label = swatchWrapper.querySelector('label');
          if (label) {
            label.style.opacity = '0.3';
            label.style.pointerEvents = 'none';
          }
        }
      }
      
      optionsContainer.appendChild(swatchWrapper);
    });
    
    swatchContainer.appendChild(optionsContainer);
    optionWrapper.appendChild(swatchContainer);
    container.appendChild(optionWrapper);
    
    // Add change event listener
    container.addEventListener('change', (e) => {
      if (e.target.matches('.native-bundle-option-input')) {
        this.handleNativeBundleOptionChange(e.target);
      }
    });
  }

  /**
   * Handle option selection change for native bundles
   */
  handleNativeBundleOptionChange(input) {
    const optionPosition = parseInt(input.dataset.optionPosition);
    const value = input.value;
    const variantImage = input.dataset.variantImage;
    
    // Update selected options
    this.selectedNativeBundleOptions[optionPosition] = value;
    
    // Update the option value display
    const valueDisplay = this.activeModal.querySelector(`[data-option-value-display="${optionPosition}"]`);
    if (valueDisplay) {
      valueDisplay.textContent = value;
    }
    
    // Update product image if variant has an image
    const productEl = input.closest('.upgrade-bundle-product');
    if (productEl) {
      // Remove "please make a selection" text from badge once user interacts
      const statusEls = productEl.querySelectorAll('[data-product-status].included');
      statusEls.forEach(el => {
        const selectionHint = el.querySelector('.selection-hint');
        if (selectionHint) {
          selectionHint.remove();
        }
        // Update text to just "Included" if it still has the hint
        if (el.innerHTML.includes('please make a selection')) {
          el.textContent = 'Included';
        }
      });
      
      if (variantImage) {
        const imgEl = productEl.querySelector('.upgrade-bundle-product__image img');
        if (imgEl) {
          imgEl.src = variantImage;
        }
      }
    }
    
    // Update price display based on option product variant
    this.updateNativeBundleOptionPrice(optionPosition);
    
    // Find and set the matching bundle variant
    this.updateSelectedNativeBundleVariant();
    this.updateNativeBundleButton();
  }

  /**
   * Update price display for a native bundle option product
   */
  updateNativeBundleOptionPrice(optionPosition) {
    const bundleData = this.nativeBundleData;
    if (!bundleData) return;
    
    const optionProduct = bundleData.option_products?.[optionPosition];
    if (!optionProduct || !optionProduct.variants) return;
    
    const selectedValue = this.selectedNativeBundleOptions[optionPosition];
    const matchingVariant = optionProduct.variants.find(v => 
      v.option1 === selectedValue || v.option2 === selectedValue || v.option3 === selectedValue || v.title === selectedValue
    );
    
    if (matchingVariant) {
      const productEl = this.activeModal.querySelector(`[data-option-position="${optionPosition}"]`);
      if (productEl) {
        const currentPriceEl = productEl.querySelector('.upgrade-bundle-product__price-current');
        const comparePriceEl = productEl.querySelector('.upgrade-bundle-product__price-compare');
        
        if (currentPriceEl) {
          currentPriceEl.textContent = this.formatMoney(matchingVariant.price);
        }
        if (comparePriceEl) {
          if (matchingVariant.compare_at_price && matchingVariant.compare_at_price > matchingVariant.price) {
            comparePriceEl.textContent = this.formatMoney(matchingVariant.compare_at_price);
            comparePriceEl.style.display = '';
          } else {
            comparePriceEl.style.display = 'none';
          }
        }
        
        // Update image
        if (matchingVariant.image) {
          const imgEl = productEl.querySelector('.upgrade-bundle-product__image img');
          if (imgEl) {
            imgEl.src = matchingVariant.image;
          }
        }
      }
    }
  }

  /**
   * Find the native bundle variant that matches current option selections
   */
  updateSelectedNativeBundleVariant() {
    const bundleData = this.nativeBundleData;
    if (!bundleData || !bundleData.native_bundle) {
      this.selectedNativeBundleVariantId = null;
      return;
    }
    
    const nativeBundle = bundleData.native_bundle;
    const variants = nativeBundle.variants;
    
    // Find variant that matches all selected options
    const matchingVariant = variants.find(variant => {
      let matches = true;
      Object.keys(this.selectedNativeBundleOptions).forEach(positionStr => {
        const position = parseInt(positionStr);
        const selectedValue = this.selectedNativeBundleOptions[position];
        const variantOptionValue = variant[`option${position}`];
        if (variantOptionValue !== selectedValue) {
          matches = false;
        }
      });
      return matches;
    });
    
    if (matchingVariant) {
      this.selectedNativeBundleVariantId = matchingVariant.id;
      
      // Update pricing based on selected variant
      this.linkedBundlePrice = matchingVariant.price;
      this.linkedBundleComparePrice = matchingVariant.compare_at_price;
      
      if (this.linkedBundleComparePrice && this.linkedBundleComparePrice > this.linkedBundlePrice) {
        this.discountPercentage = Math.round(
          ((this.linkedBundleComparePrice - this.linkedBundlePrice) / this.linkedBundleComparePrice) * 100
        );
      }
    } else {
      this.selectedNativeBundleVariantId = null;
    }
  }

  /**
   * Update the complete bundle button for native bundles
   */
  updateNativeBundleButton() {
    if (!this.activeModal || !this.isNativeBundle) return;
    
    const completeBtn = this.activeModal.querySelector('[data-bundle-complete]');
    const savingsEl = this.activeModal.querySelector('[data-bundle-total-savings]');
    
    // Enable/disable based on valid variant selection
    completeBtn.disabled = !this.selectedNativeBundleVariantId;
    
    // Show bundle pricing
    if (this.linkedBundlePrice && this.linkedBundlePrice > 0) {
      const priceHtml = this.linkedBundleComparePrice && this.linkedBundleComparePrice > this.linkedBundlePrice
        ? `— ${this.formatMoney(this.linkedBundlePrice)} <span style="text-decoration: line-through; opacity: 0.7;">${this.formatMoney(this.linkedBundleComparePrice)}</span>`
        : `— ${this.formatMoney(this.linkedBundlePrice)}`;
      savingsEl.innerHTML = priceHtml;
    } else {
      savingsEl.innerHTML = '';
    }
  }

  createProductElement(product, isInCart, cartVariantIds, badgeType = null, isMasterProduct = false) {
    const template = document.getElementById('upgrade-bundle-product-template');
    const productEl = template.content.cloneNode(true).querySelector('[data-bundle-product]');

    // Validate product has variants
    if (!product.variants || !Array.isArray(product.variants) || product.variants.length === 0) {
      console.error('Product has no valid variants:', product);
      productEl.querySelector('.upgrade-bundle-product__title').textContent = product.title + ' (No variants available)';
      return productEl;
    }

    // If allowed_variant_ids is provided, restrict selection to those variants
    const allowedIds = Array.isArray(product.allowed_variant_ids)
      ? product.allowed_variant_ids.map(id => String(id))
      : null;

    const getFirstAllowedVariant = () => {
      if (!allowedIds) {
        return product.variants.find(v => v.available) || product.variants[0];
      }
      return (
        product.variants.find(v => v.available && allowedIds.includes(String(v.id))) ||
        product.variants.find(v => allowedIds.includes(String(v.id))) ||
        product.variants[0]
      );
    };

    // Find first available variant (respecting allowed_variant_ids when present)
    const firstAvailableVariant = getFirstAllowedVariant();
    
    if (!firstAvailableVariant) {
      console.error('No variant found for product:', product);
      return productEl;
    }
    
    productEl.dataset.productId = String(product.id);
    productEl.dataset.variantId = String(firstAvailableVariant.id);
    productEl.dataset.productHandle = product.handle;
    // For master product: use compare_at_price for total calculation (if available)
    // This ensures the bundle total uses the non-discounted price for the primary component
    const priceForTotal = (isMasterProduct && firstAvailableVariant.compare_at_price) 
      ? firstAvailableVariant.compare_at_price 
      : firstAvailableVariant.price;
    productEl.dataset.price = String(priceForTotal); // Store price for total calculation

    if (isInCart) {
      productEl.classList.add('in-cart');
    }

    // Set image
    const img = productEl.querySelector('img');
    const imageUrl = firstAvailableVariant.image || product.featured_image || '';
    img.src = imageUrl;
    img.alt = product.title;

    // Set title
    productEl.querySelector('.upgrade-bundle-product__title').textContent = product.title;

    // Set price (already in cents from Liquid)
    const price = parseInt(firstAvailableVariant.price);
    const comparePrice = firstAvailableVariant.compare_at_price ? 
      parseInt(firstAvailableVariant.compare_at_price) : null;

    // For master product: only show compare_at_price (no discount shown)
    // This prevents confusion since the discount is part of the bundle pricing
    if (isMasterProduct && comparePrice && comparePrice > 0) {
      // Show only the compare_at_price as the current price
      productEl.querySelector('.upgrade-bundle-product__price-current').textContent = 
        this.formatMoney(comparePrice);
      // Hide the strikethrough price element
      productEl.querySelector('.upgrade-bundle-product__price-compare').style.display = 'none';
    } else {
      // Normal pricing display for other products
      productEl.querySelector('.upgrade-bundle-product__price-current').textContent = 
        this.formatMoney(price);
      
      if (comparePrice && comparePrice > price) {
        productEl.querySelector('.upgrade-bundle-product__price-compare').textContent = 
          this.formatMoney(comparePrice);
      } else {
        productEl.querySelector('.upgrade-bundle-product__price-compare').style.display = 'none';
      }
    }

    // Render variants if more than one allowed variant (otherwise lock to default)
    const variantSelector = productEl.querySelector('[data-variant-selector]');
    const allowedVariantCount = allowedIds ? allowedIds.length : product.variants.length;
    if (allowedVariantCount > 1 && !isInCart) {
      this.renderVariantSelector(variantSelector, product, cartVariantIds);
    } else {
      variantSelector.style.display = 'none';
    }

    // Set status badge based on state
    const statusEl = productEl.querySelector('[data-product-status]');
    if (statusEl) {
      if (badgeType === 'upgrade') {
        // Swap/upgrade product - "Bundle Upgrade"
        statusEl.textContent = this.translations.bundle_upgrade || 'Bundle Upgrade';
        statusEl.classList.add('bundle-upgrade');
      } else if (badgeType === 'will-add') {
        // Items not in cart without variant selector - "Will be added"
        statusEl.textContent = this.translations.will_be_added || 'Will be added';
        statusEl.classList.add('will-be-added');
      } else if (isInCart) {
        statusEl.textContent = this.translations.already_in_cart || 'Already in your cart';
        statusEl.classList.add('already-in-cart');
        
        // Hide variant selector for products in cart
        const variantSelector = productEl.querySelector('[data-variant-selector]');
        if (variantSelector) {
          variantSelector.style.display = 'none';
        }
      } else if (!firstAvailableVariant.available) {
        statusEl.textContent = this.translations.out_of_stock || 'Out of stock';
        statusEl.classList.add('out-of-stock');
      } else {
        // Remove badge element completely if product is not in cart and is available
        statusEl.remove();
      }
    }

    return productEl;
  }

  renderVariantSelector(container, product, cartVariantIds) {
    // Use product options structure
    if (!product.options || product.options.length === 0) {
      return;
    }
    
    // Check if this is a color option
    const isColorOption = (name) => {
      const lowerName = name.toLowerCase();
      return lowerName.includes('color') || lowerName.includes('colour');
    };
    
    const allowedIds = Array.isArray(product.allowed_variant_ids)
      ? product.allowed_variant_ids.map(id => String(id))
      : null;

    product.options.forEach((option, optionIndex) => {
      const optionWrapper = document.createElement('div');
      optionWrapper.className = 'bundle-variant-options';
      
      // Add option title
      const optionTitle = document.createElement('div');
      optionTitle.className = 'bundle-variant-option-title';
      // Match product page title for colour option
      if (isColorOption(option.name)) {
        optionTitle.textContent = (this.translations.pick_your_colour || 'Pick your colour');
      } else {
        optionTitle.textContent = option.name;
      }
      optionWrapper.appendChild(optionTitle);

      // Wrap in .swatch container to match product page structure
      const swatchContainer = document.createElement('div');
      swatchContainer.className = 'swatch';
      if (isColorOption(option.name)) {
        swatchContainer.setAttribute('data-color-option', '');
        swatchContainer.setAttribute('data-selection-step', 'true');
      }
      
      const optionsContainer = document.createElement('div');
      optionsContainer.className = 'swatch__options';
      if (isColorOption(option.name)) {
        optionsContainer.classList.add('colour');
      }
      
      option.values.forEach(value => {
        // Find variant that matches this option value
        const variant = product.variants.find(v => {
          const optionKey = `option${option.position}`;
          const matchesValue = v[optionKey] === value;
          const isAllowed = !allowedIds || allowedIds.includes(String(v.id));
          return matchesValue && isAllowed;
        });

        if (!variant) return;

        // Use same structure as product page: product-swatch wrapper
        const swatchWrapper = document.createElement('div');
        const colorHandle = value.toLowerCase().replace(/\s+/g, '-');
        swatchWrapper.className = `product-swatch colour ${colorHandle}`;
        swatchWrapper.setAttribute('data-variant-option-value-wrapper', '');

        const input = document.createElement('input');
        input.type = 'radio';
        input.className = `swatch--${colorHandle}`;
        input.name = `bundle-variant-${String(product.id)}-${option.position}`;
        input.value = String(variant.id);
        input.id = `bundle-variant-${String(variant.id)}`;
        input.setAttribute('aria-label', value);
        input.setAttribute('tabindex', '0');
        input.dataset.bundleVariant = '';
        input.dataset.variantOptionValue = '';
        input.dataset.variantOptionValueIndex = String(optionIndex);
        input.dataset.colour = colorHandle;
        input.dataset.price = String(variant.price);
        input.dataset.comparePrice = variant.compare_at_price ? String(variant.compare_at_price) : '';
        input.dataset.available = String(variant.available);
        // Store variant image for swapping
        input.dataset.variantImage = variant.image || product.featured_image || '';

        // Check if first available variant
        const firstAvailable = product.variants.find(v => v.available);
        if (firstAvailable && String(variant.id) === String(firstAvailable.id)) {
          input.checked = true;
        }

        // If it's a color option, use swatch-element structure
        if (isColorOption(option.name)) {
          const swatchElement = document.createElement('div');
          swatchElement.className = `swatch-element color ${colorHandle}-swatch`;
          
          const label = document.createElement('label');
          label.htmlFor = input.id;
          label.className = `swatch-${colorHandle}`;
          label.setAttribute('data-variant-option-value-label', '');
          label.setAttribute('data-variant-id', String(variant.id));
          label.setAttribute('data-variant-image', 'true');
          label.setAttribute('data-swapper', colorHandle);
          label.setAttribute('data-title', value);
          
          // Add checkmark structure
          const roundContainer = document.createElement('div');
          roundContainer.className = 'swatch__round--container';
          
          const checkmark = document.createElement('img');
          checkmark.className = 'swatch__checkmark';
          checkmark.src = 'https://cdn.shopify.com/s/files/1/0502/5929/6444/files/checkbox.svg?v=1700570579';
          checkmark.alt = 'checkmark';
          
          roundContainer.appendChild(checkmark);
          label.appendChild(roundContainer);
          swatchElement.appendChild(label);
          
          swatchWrapper.appendChild(input);
          swatchWrapper.appendChild(swatchElement);
        } else {
          // Non-color options: simple label structure
          const label = document.createElement('label');
          label.htmlFor = input.id;
          label.textContent = value;
          
          swatchWrapper.appendChild(input);
          swatchWrapper.appendChild(label);
        }

        // Disable if not available
        if (!variant.available) {
          input.disabled = true;
          if (isColorOption(option.name)) {
            const swatchElement = swatchWrapper.querySelector('.swatch-element');
            if (swatchElement) {
              swatchElement.style.opacity = '0.3';
              swatchElement.style.pointerEvents = 'none';
            }
          } else {
            const label = swatchWrapper.querySelector('label');
            if (label) {
              label.style.opacity = '0.3';
              label.style.pointerEvents = 'none';
            }
          }
        }

        optionsContainer.appendChild(swatchWrapper);
      });

      swatchContainer.appendChild(optionsContainer);
      optionWrapper.appendChild(swatchContainer);
      container.appendChild(optionWrapper);
    });
  }

  addProductToSelection(productEl) {
    const isInCart = productEl.classList.contains('in-cart');
    if (isInCart) return;

    const variantId = productEl.dataset.variantId;
    const selectedVariantInput = productEl.querySelector('input[data-bundle-variant]:checked');
    const actualVariantId = selectedVariantInput ? selectedVariantInput.value : variantId;
    
    this.selectedProducts.set(productEl.dataset.productId, {
      variantId: actualVariantId,
      element: productEl,
      price: selectedVariantInput ? parseFloat(selectedVariantInput.dataset.price) : 0
    });
  }

  updateProductVariant(productEl, input) {
    const variantId = input.value;
    const price = parseInt(input.dataset.price);
    const comparePrice = input.dataset.comparePrice ? parseInt(input.dataset.comparePrice) : null;
    const variantImage = input.dataset.variantImage;

    // Update product element
    productEl.dataset.variantId = variantId;
    productEl.dataset.price = String(price); // Update price for total calculation
    
    // Update product image if variant has an image
    if (variantImage) {
      const imgEl = productEl.querySelector('.upgrade-bundle-product__image img');
      if (imgEl) {
        imgEl.src = variantImage;
      }
    }
    
    // Update price display (price is already in cents)
    productEl.querySelector('.upgrade-bundle-product__price-current').textContent = 
      this.formatMoney(price);
    
    if (comparePrice && comparePrice > price) {
      const comparePriceEl = productEl.querySelector('.upgrade-bundle-product__price-compare');
      comparePriceEl.textContent = this.formatMoney(comparePrice);
      comparePriceEl.style.display = '';
    } else {
      productEl.querySelector('.upgrade-bundle-product__price-compare').style.display = 'none';
    }

    // Update selected products map (all products not in cart are selected)
    const isInCart = productEl.classList.contains('in-cart');
    if (!isInCart) {
      this.selectedProducts.set(productEl.dataset.productId, {
        variantId: variantId,
        element: productEl,
        price: price
      });
      this.updateCompleteBundleButton();
    }
  }

  updateCompleteBundleButton() {
    if (!this.activeModal) return;

    const completeBtn = this.activeModal.querySelector('[data-bundle-complete]');
    const savingsEl = this.activeModal.querySelector('[data-bundle-total-savings]');

    // Enable/disable button based on selected products
    completeBtn.disabled = this.selectedProducts.size === 0;

    // Calculate original total from actual variant prices in the modal
    let originalTotal = 0;
    const productElements = this.activeModal.querySelectorAll('[data-bundle-product]');
    productElements.forEach(productEl => {
      if (productEl.dataset.price) {
        const itemPrice = parseInt(productEl.dataset.price) || 0;
        originalTotal += itemPrice;
      }
    });

    // Determine discounted total:
    // 1. If linkedBundlePrice is set (from "Linked Bundle Price Accuracy" metafield), use it directly
    // 2. Otherwise, calculate from discount percentage
    let discountedTotal = 0;
    let showPricing = false;

    if (this.linkedBundlePrice !== null && this.linkedBundlePrice > 0) {
      // Use the exact linked bundle price from the metafield
      discountedTotal = this.linkedBundlePrice;
      // Use the linked variant's compare_at_price as the original price if available
      if (this.linkedBundleComparePrice !== null && this.linkedBundleComparePrice > 0) {
        originalTotal = this.linkedBundleComparePrice;
      }
      showPricing = originalTotal > 0;
    } else if (this.discountPercentage > 0) {
      // Calculate discounted price per item
      productElements.forEach(productEl => {
        if (productEl.dataset.price) {
          const itemPrice = parseInt(productEl.dataset.price) || 0;
          // Shopify calculates discount per item: floor the discount amount, then subtract
          const discountAmount = Math.floor(itemPrice * this.discountPercentage / 100);
          discountedTotal += (itemPrice - discountAmount);
        }
      });
      showPricing = originalTotal > 0;
    }
    
    // Only show pricing if we have a valid total
    if (showPricing && originalTotal > 0) {
      savingsEl.innerHTML = `— ${this.formatMoney(discountedTotal)} <span style="text-decoration: line-through; opacity: 0.7;">${this.formatMoney(originalTotal)}</span>`;
    } else {
      savingsEl.innerHTML = '';
    }
  }

    async completeBundleAddToCart() {
      // =============================================================================
      // NATIVE BUNDLE ADD TO CART
      // For native bundles, we replace the parent item with the bundle variant
      // =============================================================================
      if (this.isNativeBundle) {
        await this.completeNativeBundleAddToCart();
        return;
      }
      
      // =============================================================================
      // CUSTOM BUNDLE ADD TO CART (existing logic)
      // =============================================================================
      if (this.selectedProducts.size === 0) return;

      const completeBtn = this.activeModal.querySelector('[data-bundle-complete]');
      const originalText = completeBtn.querySelector('span').textContent;
      
      completeBtn.disabled = true;
      completeBtn.classList.add('loading');
      completeBtn.querySelector('span').textContent = this.translations.adding || 'Adding...';

      try {
        const cart = await fetch('/cart.js').then(res => res.json());
        if (!this.parentVariantId) throw new Error('Parent variant ID not found');
        
        const parentItem = cart.items.find(item => item.variant_id === this.parentVariantId);
        if (!parentItem) throw new Error('Parent product not found in cart');
        if (parentItem.properties && parentItem.properties['_bundle_id']) {
          throw new Error('This product is already part of a bundle');
        }

        const parentLineKey = parentItem.key;
        
        // NOTE: We do NOT use Shopify's parent_line_key nesting feature.
        // Checkout nesting causes issues with discounts and compatibility.
        // Instead, we use _bundle_id property for virtual grouping in the cart drawer only.
        // Items remain as individual line items at checkout.
        
        // Use pre-determined swap info from openBundleModal
        // Swap only happens if swap variant has its own bundle assigned
        const swapVariantId = this.swapVariantId;
        const shouldSwapParent = this.shouldSwapParent && swapVariantId && swapVariantId !== this.parentVariantId;
        
        let parentColorName = null;
        if (parentItem.options_with_values && Array.isArray(parentItem.options_with_values)) {
          const colorOption = parentItem.options_with_values.find(opt => 
            opt.name && (opt.name.toLowerCase() === 'color' || opt.name.toLowerCase() === 'colour')
          );
          if (colorOption && colorOption.value && colorOption.value !== 'Default Title') {
            parentColorName = colorOption.value;
          }
        }

        const bundleId = `bundle_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        const itemsToAdd = [];
        const standaloneItemsToReduce = [];
        const allBundleProductElements = this.activeModal.querySelectorAll('[data-bundle-product]');
        
        allBundleProductElements.forEach((productEl) => {
          if (productEl.dataset.isMasterProduct === 'true') return;
          
          let variantId = null;
          const productId = productEl.dataset.productId;
          const selectedProductData = this.selectedProducts.get(productId);
          
          if (selectedProductData) {
            variantId = selectedProductData.variantId;
          } else if (productEl.classList.contains('in-cart')) {
            const cartItem = cart.items.find(item => {
              const matchesProduct = String(item.product_id) === String(productId);
              const hasBundleId = item.properties && item.properties['_bundle_id'];
              return matchesProduct && !hasBundleId;
            });
            
            if (cartItem) {
              variantId = cartItem.variant_id;
            } else {
              const selectedVariantInput = productEl.querySelector('input[data-bundle-variant]:checked');
              variantId = selectedVariantInput ? parseInt(selectedVariantInput.value) : null;
            }
          } else {
            const selectedVariantInput = productEl.querySelector('input[data-bundle-variant]:checked');
            variantId = selectedVariantInput ? parseInt(selectedVariantInput.value) : parseInt(productEl.dataset.variantId);
          }
          
          const parsedVariantId = parseInt(variantId);
          if (!parsedVariantId || isNaN(parsedVariantId) || parsedVariantId === this.parentVariantId) return;
          
          const allMatchingItems = cart.items.filter(item => item.variant_id === parsedVariantId);
          const standaloneItem = allMatchingItems.find(item => {
            const hasBundleId = item.properties && item.properties['_bundle_id'];
            return !hasBundleId;
          });
          
          if (standaloneItem) {
            standaloneItemsToReduce.push({
              key: standaloneItem.key,
              newQty: standaloneItem.quantity - 1
            });
          }
          
          itemsToAdd.push({
            id: parsedVariantId,
            quantity: 1,
            properties: {
              '_bundle_id': bundleId,
              '_is-bundle': 'true',
              'Bundle Upgrade': parentColorName || 'Added'
            }
          });
        });

        // =============================================================================
        // STORE PRE-UPGRADE CART STATE FOR REVERT FUNCTIONALITY
        // Must be done BEFORE any cart modifications
        // =============================================================================
        if (this.bundleRevertEnabled) {
          const componentVariantIds = itemsToAdd.map(item => item.id);
          this.storePreUpgradeCartState(bundleId, cart, componentVariantIds, this.parentVariantId, false, null);
        }

        // Reduce standalone items using keys (not line numbers) for reliability
        // Deduplicate to avoid reducing the same item multiple times
        if (standaloneItemsToReduce.length > 0) {
          const deduplicatedItems = new Map();
          standaloneItemsToReduce.forEach(item => {
            const existing = deduplicatedItems.get(item.key);
            if (!existing || item.newQty < existing.newQty) {
              deduplicatedItems.set(item.key, item);
            }
          });
          
          for (const item of deduplicatedItems.values()) {
            const res = await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'xmlhttprequest' },
              body: JSON.stringify({ id: item.key, quantity: item.newQty }), // Use key, not line
              credentials: 'same-origin'
            });
            if (!res.ok) throw new Error('Failed to reduce item quantity');
          }
        }

        // Handle parent swap if swap_variant_id is set
        if (shouldSwapParent) {
          // Remove the original parent item first
          let currentCart = await fetch('/cart.js').then(res => res.json());
          const originalParentLineNumber = currentCart.items.findIndex(item => item.key === parentLineKey) + 1;
          
          if (originalParentLineNumber > 0) {
            const removeRes = await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'xmlhttprequest' },
              body: JSON.stringify({ line: originalParentLineNumber, quantity: 0 }),
              credentials: 'same-origin'
            });
            if (!removeRes.ok) throw new Error('Failed to remove original parent item');
          }
          
          // Add the swap variant as the new parent with bundle properties
          const swapParentItem = {
            id: swapVariantId,
            quantity: 1,
            properties: {
              '_bundle_id': bundleId,
              '_bundle_parent': 'true',
              '_is-bundle': 'true',
              'Bundle Upgrade': 'Added'
            }
          };
          
          // Add swap parent first
          const addSwapRes = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'xmlhttprequest' },
            body: JSON.stringify({ items: [swapParentItem] }),
            credentials: 'same-origin'
          });
          
          if (!addSwapRes.ok) {
            const addSwapResult = await addSwapRes.json();
            throw new Error(addSwapResult.description || 'Failed to add swap parent item');
          }
          
          // Add bundle child items as individual line items (no parent_line_key nesting)
          if (itemsToAdd.length > 0) {
            const addChildrenRes = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'xmlhttprequest' },
              body: JSON.stringify({ items: itemsToAdd }),
              credentials: 'same-origin'
            });
            
            if (!addChildrenRes.ok) {
              const addChildrenResult = await addChildrenRes.json();
              throw new Error(addChildrenResult.description || 'Failed to add bundle child items');
            }
          }
        } else {
          // Normal flow: no swap, just add children and update parent
          // Items are added as individual line items (no checkout nesting)
          // Virtual bundle grouping in cart is done via _bundle_id property
          if (itemsToAdd.length > 0) {
            const addRes = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'xmlhttprequest' },
              body: JSON.stringify({ items: itemsToAdd }),
              credentials: 'same-origin'
            });
            
            const addResult = await addRes.json();
            if (!addRes.ok) throw new Error(addResult.description || 'Failed to add bundle items');
          }

          // Update parent with bundle properties
          const updatedCart = await fetch('/cart.js').then(res => res.json());
          const parentLineNumber = updatedCart.items.findIndex(item => item.key === parentLineKey) + 1;
          
          if (parentLineNumber > 0) {
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'xmlhttprequest' },
              body: JSON.stringify({
                line: parentLineNumber,
                properties: {
                  '_bundle_id': bundleId,
                  '_bundle_parent': 'true',
                  '_is-bundle': 'true',
                  'Bundle Upgrade': 'Added'
                }
              }),
              credentials: 'same-origin'
            });
          }
        }

        // Add cart attribute to tag the cart with "Bundle Upgrade"
        await this.addBundleUpgradeCartAttribute();

        // Set timestamp for animation trigger (uses the bundleId that was assigned to items)
        const timestampKey = `bundle_upgrade_time_${bundleId}`;
        const timestampValue = Date.now().toString();
        sessionStorage.setItem(timestampKey, timestampValue);

        // Close modal FIRST so cart drawer becomes visible
        this.closeBundleModal();
        
        // Small delay to let modal animation finish, then update cart
        setTimeout(() => {
          if (window.PXUTheme.jsCart) window.PXUTheme.jsCart.updateCart();
        }, 150);
        
        setTimeout(() => {
          completeBtn.disabled = false;
          completeBtn.classList.remove('loading');
          completeBtn.querySelector('span').textContent = originalText;
        }, 300);
    } catch (error) {
      console.error('Error adding bundle to cart:', error);
      completeBtn.disabled = false;
      completeBtn.classList.remove('loading');
      completeBtn.querySelector('span').textContent = originalText;
      alert('Sorry, there was an error adding the bundle to your cart. Please try again.');
    }
  }

  // =============================================================================
  // NATIVE BUNDLE ADD TO CART
  // Replaces the parent item with the native Shopify bundle variant
  // Also handles deduplication of bundle components already in cart
  // =============================================================================
  async completeNativeBundleAddToCart() {
    if (!this.selectedNativeBundleVariantId) {
      console.error('No native bundle variant selected');
      return;
    }

    const completeBtn = this.activeModal.querySelector('[data-bundle-complete]');
    const originalText = completeBtn.querySelector('span').textContent;
    
    completeBtn.disabled = true;
    completeBtn.classList.add('loading');
    completeBtn.querySelector('span').textContent = this.translations.adding || 'Adding...';

    try {
      const cart = await fetch('/cart.js').then(res => res.json());
      
      if (!this.parentVariantId) {
        throw new Error('Parent variant ID not found');
      }
      
      const parentItem = cart.items.find(item => item.variant_id === this.parentVariantId);
      if (!parentItem) {
        throw new Error('Parent product not found in cart');
      }

      // =============================================================================
      // COLLECT COMPONENT VARIANT IDs FOR THE NATIVE BUNDLE
      // These are the variants that will be part of the bundle (for deduplication)
      // =============================================================================
      const bundleData = this.nativeBundleData;
      const componentVariantIds = [];
      const standaloneItemsToReduce = [];
      
      // 1. Collect selected option product variants
      if (bundleData && bundleData.option_products) {
        Object.keys(this.selectedNativeBundleOptions).forEach(positionStr => {
          const position = parseInt(positionStr);
          const selectedValue = this.selectedNativeBundleOptions[position];
          const optionProduct = bundleData.option_products[position];
          
          if (optionProduct && optionProduct.variants) {
            // Find the variant that matches the selected option value
            const matchingVariant = optionProduct.variants.find(v => 
              v.option1 === selectedValue || 
              v.option2 === selectedValue || 
              v.option3 === selectedValue || 
              v.title === selectedValue
            );
            
            if (matchingVariant && matchingVariant.id) {
              componentVariantIds.push(matchingVariant.id);
            }
          }
        });
      }
      
      // 2. Collect fixed product variants (always included)
      if (bundleData && bundleData.fixed_variants) {
        bundleData.fixed_variants.forEach(fixedVariant => {
          if (fixedVariant && fixedVariant.id) {
            componentVariantIds.push(fixedVariant.id);
          }
        });
      }
      
      // 3. Find standalone cart items that match these component variants
      // Only consider items that are NOT already part of another bundle
      componentVariantIds.forEach(variantId => {
        const standaloneItem = cart.items.find(item => {
          const hasBundleId = item.properties && item.properties['_bundle_id'];
          const isNativeBundle = item.properties && item.properties['_bundle_upgrade_native'];
          return item.variant_id === variantId && !hasBundleId && !isNativeBundle;
        });
        
        if (standaloneItem) {
          standaloneItemsToReduce.push({
            key: standaloneItem.key,
            variant_id: standaloneItem.variant_id,
            newQty: standaloneItem.quantity - 1
          });
        }
      });

      // =============================================================================
      // STORE PRE-UPGRADE CART STATE FOR REVERT FUNCTIONALITY (Native Bundle)
      // Must be done BEFORE any cart modifications
      // For native bundles, we use the bundle variant ID as the bundle identifier
      // Now includes component variant IDs for proper revert restoration
      // =============================================================================
      const nativeBundleId = `native_bundle_${this.selectedNativeBundleVariantId}`;
      if (this.bundleRevertEnabled) {
        // Pass the component variant IDs so they can be restored on revert
        this.storePreUpgradeCartState(nativeBundleId, cart, componentVariantIds, this.parentVariantId, true, this.selectedNativeBundleVariantId);
      }

      // =============================================================================
      // REMOVE/REDUCE STANDALONE ITEMS THAT WILL BE PART OF THE BUNDLE
      // Use item keys (not line numbers) for reliability
      // Deduplicate to avoid reducing the same item multiple times
      // =============================================================================
      if (standaloneItemsToReduce.length > 0) {
        // Deduplicate by key - keep the lowest newQty in case of duplicates
        const deduplicatedItems = new Map();
        standaloneItemsToReduce.forEach(item => {
          const existing = deduplicatedItems.get(item.key);
          if (!existing || item.newQty < existing.newQty) {
            deduplicatedItems.set(item.key, item);
          }
        });
        
        for (const item of deduplicatedItems.values()) {
          const reduceRes = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'xmlhttprequest' },
            body: JSON.stringify({ id: item.key, quantity: item.newQty }), // Use key, not line
            credentials: 'same-origin'
          });
          if (!reduceRes.ok) {
            console.warn('[Native Bundle] Failed to reduce standalone item quantity');
          }
        }
      }

      // Step 1: Remove the parent item (the trigger product like SURI 2.0)
      // Re-fetch cart to get updated state after reducing standalone items
      const updatedCart = await fetch('/cart.js').then(res => res.json());
      const updatedParentItem = updatedCart.items.find(item => item.variant_id === this.parentVariantId);
      
      if (updatedParentItem) {
        const removeResponse = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'X-Requested-With': 'xmlhttprequest'
          },
          body: JSON.stringify({
            id: updatedParentItem.key,
            quantity: 0
          }),
          credentials: 'same-origin'
        });

        if (!removeResponse.ok) {
          throw new Error('Failed to remove parent item from cart');
        }
      }

      // Step 2: Add the native bundle variant
      // Properties added:
      // - _bundle_upgrade_native: hidden flag for native bundle detection
      // - _original_parent_variant_id: the original trigger product variant (for addon product lookup)
      // - Bundle Upgrade: visible flag (same as custom bundles)
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Requested-With': 'xmlhttprequest'
        },
        body: JSON.stringify({
          items: [{
            id: this.selectedNativeBundleVariantId,
            quantity: 1,
            properties: {
              '_bundle_upgrade_native': 'true',
              '_original_parent_variant_id': String(this.parentVariantId),
              '_original_parent_product_id': String(bundleData.master_product?.id || ''),
              'Bundle Upgrade': 'true'
            }
          }]
        }),
        credentials: 'same-origin'
      });

      if (!addResponse.ok) {
        const errorData = await addResponse.json().catch(() => ({}));
        throw new Error(errorData.description || 'Failed to add native bundle to cart');
      }

      // Add bundle cart attribute
      await this.addBundleUpgradeCartAttribute();
      
      // Set timestamp for animation trigger (reuses nativeBundleId from earlier in function)
      const timestampKey = `bundle_upgrade_time_${nativeBundleId}`;
      const timestampValue = Date.now().toString();
      sessionStorage.setItem(timestampKey, timestampValue);

      // Close modal FIRST so cart drawer becomes visible
      this.closeBundleModal();
      
      // Small delay to let modal animation finish, then refresh the cart UI
      setTimeout(() => {
        if (window.PXUTheme && window.PXUTheme.jsCart && typeof window.PXUTheme.jsCart.updateCart === 'function') {
          window.PXUTheme.jsCart.updateCart();
        } else if (typeof window.refreshCart === 'function') {
          window.refreshCart();
        }
      }, 150);
      
      setTimeout(() => {
        completeBtn.disabled = false;
        completeBtn.classList.remove('loading');
        completeBtn.querySelector('span').textContent = originalText;
      }, 300);

    } catch (error) {
      console.error('Error adding native bundle to cart:', error);
      completeBtn.disabled = false;
      completeBtn.classList.remove('loading');
      completeBtn.querySelector('span').textContent = originalText;
      alert('Sorry, there was an error adding the bundle to your cart. Please try again.');
    }
  }

  async addBundleUpgradeCartAttribute() {
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'xmlhttprequest' },
        body: JSON.stringify({
          attributes: {
            'Bundle Upgrade': 'true'
          }
        }),
        credentials: 'same-origin'
      });
    } catch (error) {
      console.error('Failed to add Bundle Upgrade cart attribute:', error);
    }
  }

  async fetchCart() {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      return await response.json();
    } catch (error) {
      return { items: [] };
    }
  }

  formatMoney(cents) {
    if (typeof cents === 'string') {
      cents = parseFloat(cents);
    }
    
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = this.moneyFormat;

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision || 2;
      thousands = thousands || ',';
      decimal = decimal || '.';

      if (isNaN(number) || number === null) {
        return '0';
      }

      number = (number / 100.0).toFixed(precision);

      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? decimal + parts[1] : '';

      return dollarsAmount + centsAmount;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'");
        break;
    }

    return formatString.replace(placeholderRegex, value);
  }

  // =============================================================================
  // BUNDLE REVERT METHODS
  // Store pre-upgrade cart state and allow reverting bundle upgrades
  // =============================================================================

  /**
   * Store the cart state before a bundle upgrade
   * This captures all items that will be affected by the upgrade
   * @param {string} bundleId - Unique identifier for this bundle
   * @param {Object} cart - Current cart object
   * @param {Array} componentVariantIds - Variant IDs that will be part of the bundle
   * @param {number} parentVariantId - The parent/trigger product variant ID
   * @param {boolean} isNativeBundle - Whether this is a native Shopify bundle
   * @param {number} nativeBundleVariantId - For native bundles, the bundle variant being added
   */
  storePreUpgradeCartState(bundleId, cart, componentVariantIds, parentVariantId, isNativeBundle = false, nativeBundleVariantId = null) {
    if (!this.bundleRevertEnabled) return;
    
    try {
      // Get existing revert states from session storage
      const existingStates = JSON.parse(sessionStorage.getItem(this.REVERT_STORAGE_KEY) || '{}');
      
      // Find the parent item in the cart
      const parentItem = cart.items.find(item => item.variant_id === parentVariantId);
      
      // Use a Map to track items by variant_id (prevents duplicates)
      // Key: variant_id, Value: { variant_id, quantity_to_restore, properties, key, existed_before, is_parent }
      // IMPORTANT: quantity_to_restore is the amount to ADD BACK on revert, not the original quantity
      // This allows users to modify cart after upgrade and still get correct quantities on revert
      const preUpgradeItemsMap = new Map();
      
      // Store parent item state first
      // Parent is fully removed, so quantity_to_restore = full original quantity
      if (parentItem) {
        preUpgradeItemsMap.set(parentItem.variant_id, {
          variant_id: parentItem.variant_id,
          quantity_to_restore: parentItem.quantity, // Full quantity since parent is removed
          properties: parentItem.properties || {},
          key: parentItem.key,
          existed_before: true,
          is_parent: true
        });
      }
      
      // Store component items that already exist in cart
      // Skip if already stored (e.g., parent is also a component, or duplicate component IDs)
      componentVariantIds.forEach(variantId => {
        // Skip if we already have this variant stored (e.g., parent variant)
        if (preUpgradeItemsMap.has(variantId)) {
          return;
        }
        
        const existingItem = cart.items.find(item => {
          // Find standalone items (not already part of another bundle)
          // Exclude: items with custom bundle ID, items with native bundle flag, or Shopify bundle components
          const hasCustomBundleId = item.properties && item.properties['_bundle_id'];
          const hasNativeBundleFlag = item.properties && item.properties['_bundle_upgrade_native'];
          const isShopifyBundleComponent = item.parent_relationship;
          return item.variant_id === variantId && 
                 !hasCustomBundleId && !hasNativeBundleFlag && !isShopifyBundleComponent;
        });
        
        if (existingItem) {
          // Component existed - we reduce by 1 for the bundle, so restore 1 on revert
          preUpgradeItemsMap.set(existingItem.variant_id, {
            variant_id: existingItem.variant_id,
            quantity_to_restore: 1, // Only 1 goes into the bundle, so restore 1
            properties: existingItem.properties || {},
            key: existingItem.key,
            existed_before: true,
            is_parent: false
          });
        } else {
          // Item didn't exist - mark it so we know it was added for the bundle
          // On revert, we don't need to do anything for these (bundle removal handles it)
          preUpgradeItemsMap.set(variantId, {
            variant_id: variantId,
            quantity_to_restore: 0,
            properties: {},
            existed_before: false,
            is_parent: false
          });
        }
      });
      
      // Convert map to array
      const preUpgradeItems = Array.from(preUpgradeItemsMap.values());
      
      // Store the revert state
      existingStates[bundleId] = {
        bundle_id: bundleId,
        is_native_bundle: isNativeBundle,
        native_bundle_variant_id: nativeBundleVariantId,
        parent_variant_id: parentVariantId,
        pre_upgrade_items: preUpgradeItems,
        timestamp: Date.now()
      };
      
      sessionStorage.setItem(this.REVERT_STORAGE_KEY, JSON.stringify(existingStates));
      
    } catch (error) {
      console.error('[Bundle Revert] Error storing pre-upgrade state:', error);
    }
  }

  /**
   * Get stored revert state for a bundle
   * @param {string} bundleId - Bundle ID to look up
   * @returns {Object|null} The stored revert state or null
   */
  getRevertState(bundleId) {
    if (!this.bundleRevertEnabled) return null;
    
    try {
      const existingStates = JSON.parse(sessionStorage.getItem(this.REVERT_STORAGE_KEY) || '{}');
      return existingStates[bundleId] || null;
    } catch (error) {
      console.error('[Bundle Revert] Error getting revert state:', error);
      return null;
    }
  }

  /**
   * Remove stored revert state for a bundle
   * @param {string} bundleId - Bundle ID to remove
   */
  removeRevertState(bundleId) {
    try {
      const existingStates = JSON.parse(sessionStorage.getItem(this.REVERT_STORAGE_KEY) || '{}');
      delete existingStates[bundleId];
      sessionStorage.setItem(this.REVERT_STORAGE_KEY, JSON.stringify(existingStates));
    } catch (error) {
      console.error('[Bundle Revert] Error removing revert state:', error);
    }
  }

  /**
   * Revert a bundle upgrade back to the pre-upgrade cart state
   * @param {string} bundleId - The bundle ID to revert
   * @returns {Promise<boolean>} Success status
   */
  async revertBundle(bundleId) {
    if (!this.bundleRevertEnabled) {
      console.warn('[Bundle Revert] Revert is disabled');
      return false;
    }
    
    const revertState = this.getRevertState(bundleId);
    if (!revertState) {
      console.warn('[Bundle Revert] No revert state found for bundle:', bundleId);
      return false;
    }
    
    try {
      // Step 1: Get current cart
      const cart = await fetch('/cart.js').then(res => res.json());
      
      // Step 2: Remove all items that are part of this bundle
      const bundleItems = cart.items.filter(item => {
        if (revertState.is_native_bundle) {
          // For native bundles, find the bundle variant and its components
          return item.variant_id === revertState.native_bundle_variant_id ||
                 (item.parent_relationship && 
                  cart.items.some(parent => 
                    parent.variant_id === revertState.native_bundle_variant_id && 
                    item.parent_relationship.parent_key === parent.key
                  ));
        } else {
          // For custom bundles, find items with matching bundle_id
          return item.properties && item.properties['_bundle_id'] === bundleId;
        }
      });
      
      // Remove bundle items (in reverse order to avoid index shifting issues)
      for (let i = bundleItems.length - 1; i >= 0; i--) {
        const item = bundleItems[i];
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'X-Requested-With': 'xmlhttprequest'
          },
          body: JSON.stringify({
            id: item.key,
            quantity: 0
          }),
          credentials: 'same-origin'
        });
      }
      
      // Step 3: Re-add/restore items that existed before the upgrade
      // Filter to only items that existed AND have quantity to restore
      // We ADD the quantity_to_restore to whatever currently exists (not SET to original)
      // This allows users to modify cart after upgrade and get correct quantities on revert
      const itemsToRestoreRaw = revertState.pre_upgrade_items.filter(item => 
        item.existed_before && (item.quantity_to_restore > 0 || item.quantity > 0)
      );
      
      // Deduplicate by variant_id (keep highest quantity_to_restore)
      const itemsToRestoreMap = new Map();
      itemsToRestoreRaw.forEach(item => {
        const existing = itemsToRestoreMap.get(item.variant_id);
        const restoreQty = item.quantity_to_restore !== undefined ? item.quantity_to_restore : item.quantity;
        if (!existing || restoreQty > (existing.quantity_to_restore || existing.quantity || 0)) {
          itemsToRestoreMap.set(item.variant_id, item);
        }
      });
      const itemsToRestore = Array.from(itemsToRestoreMap.values());
      
      if (itemsToRestore.length > 0) {
        for (const item of itemsToRestore) {
          // Get the quantity to restore (support both old and new format)
          const qtyToRestore = item.quantity_to_restore !== undefined ? item.quantity_to_restore : item.quantity;
          
          if (qtyToRestore <= 0) {
            continue;
          }
          
          // Clean properties - remove any bundle-related properties
          const cleanProperties = { ...item.properties };
          delete cleanProperties['_bundle_id'];
          delete cleanProperties['_bundle_parent'];
          delete cleanProperties['_is-bundle'];
          delete cleanProperties['Bundle Upgrade'];
          delete cleanProperties['_bundle_upgrade_native'];
          
          // IMPORTANT: Fetch fresh cart state for each item to avoid stale data
          const currentCart = await fetch('/cart.js').then(res => res.json());
          
          // Check if ANY item with this variant_id exists in the cart
          const anyExistingItem = currentCart.items.find(cartItem => 
            cartItem.variant_id === item.variant_id && 
            !cartItem.parent_relationship // Exclude Shopify bundle components only
          );
          
          if (anyExistingItem) {
            // Item exists - ADD the restore quantity to current quantity
            // Example: had 3 keys, 1 went to bundle (2 remain), user added 2 more (4 now)
            // On revert: 4 + 1 = 5 keys
            const targetQty = anyExistingItem.quantity + qtyToRestore;
            
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 
                'Content-Type': 'application/json',
                'X-Requested-With': 'xmlhttprequest'
              },
              body: JSON.stringify({
                id: anyExistingItem.key,
                quantity: targetQty
              }),
              credentials: 'same-origin'
            });
          } else {
            // No item with this variant_id exists - add with the restore quantity
            const addResponse = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 
                'Content-Type': 'application/json',
                'X-Requested-With': 'xmlhttprequest'
              },
              body: JSON.stringify({
                items: [{
                  id: item.variant_id,
                  quantity: qtyToRestore,
                  properties: Object.keys(cleanProperties).length > 0 ? cleanProperties : undefined
                }]
              }),
              credentials: 'same-origin'
            });
            
            if (!addResponse.ok) {
              const errorData = await addResponse.json().catch(() => ({}));
              console.error('[Bundle Revert] Failed to restore item:', item.variant_id, errorData);
            }
          }
        }
      }
      
      // Step 4: Clean up - remove the revert state and bundle cart attribute
      this.removeRevertState(bundleId);
      
      // Check if any bundles remain and remove cart attribute if needed
      await window.PXUTheme.checkAndRemoveBundleCartAttribute(bundleId);
      
      // Step 5: Refresh cart UI
      if (window.PXUTheme && window.PXUTheme.jsCart && typeof window.PXUTheme.jsCart.updateCart === 'function') {
        window.PXUTheme.jsCart.updateCart();
      }
      
      return true;
      
    } catch (error) {
      console.error('[Bundle Revert] Error reverting bundle:', error);
      return false;
    }
  }
};

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.PXUTheme.upgradeBundle = new window.PXUTheme.UpgradeBundle();
  });
} else {
  window.PXUTheme.upgradeBundle = new window.PXUTheme.UpgradeBundle();
}

